import React, { useState } from 'react';
import { BrowserRouter, Switch, Route, useParams } from 'react-router-dom';
import { useFetch } from 'hooks';
import { hasFeatureFlags } from 'utils/features';
import { routes } from 'constants/config';
import { Icon, Spinner } from 'ui';
import RemoteAutoComplete from 'components/shared/RemoteAutoComplete';
import request from 'utils/request_ajax';
import InfoTooltip from 'components/shared/InfoTooltip';

const Suggestion = ({ name }) => <div>{name}</div>;

const LessonGraders = () => {
  const learnersAsGraders = hasFeatureFlags([
    'specify_grader_on_lessons_learner',
  ]);
  const { lessonId } = useParams();
  const [waiting, setWaiting] = useState(false);
  const {
    data: lessonGraders,
    loading,
    initialLoading,
    runRequest,
  } = useFetch(routes.LESSON_OVERVIEW.LESSON_GRADERS(lessonId));
  const { data: lessonData, loading: lessonDataLoading } = useFetch(
    routes.LESSON(lessonId),
  );
  if (initialLoading || lessonDataLoading) {
    return (
      <div className="d-f ai-c jc-c mb-8">
        <Spinner />
      </div>
    );
  }
  const archived = !!lessonData?.archived_at;
  const lessonGraderIds = lessonGraders.map(grader => grader.id);

  const filterSuggestions = graders =>
    graders.filter(grader => !lessonGraderIds.includes(grader.id));

  const addGrader = grader => {
    setWaiting(true);
    request
      .postJSON(routes.LESSON_OVERVIEW.LESSON_GRADERS(lessonId), {
        grader_id: grader.id,
      })
      .then(() => {
        runRequest();
        setWaiting(false);
      });
  };

  const removeGrader = grader => {
    if (
      /* eslint-disable-next-line no-alert */
      window.confirm(
        t('lessons.graders.remove_confirmation', { name: grader.name }),
      )
    ) {
      setWaiting(true);
      request
        .delete(routes.LESSON_OVERVIEW.LESSON_GRADER(lessonId, grader.id))
        .then(() => {
          runRequest();
          setWaiting(false);
        });
    }
  };

  return (
    <div className="Module SidebarModule Autocomplete ui-front" id="graders">
      <h3 className="Module-heading SectionHeading">
        {archived ? (
          <>
            {t('lessons.graders.title.archived')}
            <InfoTooltip
              tooltip={t('archive.uneditable')}
              className="ml-4 tooltip-fixedWidth Module-headingIcon"
            />
          </>
        ) : (
          <>
            {t('lessons.graders.title.enabled')}
            <InfoTooltip
              tooltip={t('lessons.graders.tooltip')}
              className="ml-4 tooltip-fixedWidth Module-headingIcon"
            />
            {learnersAsGraders && (
              <div className="SectionHeading--designatedGraders">
                <span className="c-brand-blue fw-b">
                  {t('lessons.graders.new')}
                </span>{' '}
                {t('lessons.graders.description')}
              </div>
            )}
          </>
        )}
      </h3>
      <RemoteAutoComplete
        disabled={archived}
        placeholder={t('lessons.graders.placeholder')}
        className="SidebarModule-input"
        mapResponseToSuggestions={response => response}
        getSuggestionValue={suggestion => suggestion.name}
        renderSuggestion={Suggestion}
        filterSuggestions={filterSuggestions}
        onSuggestionSelected={addGrader}
        url={routes.LESSON_OVERVIEW.GRADER_OPTIONS(lessonId)}
      />
      <section id="graders_list">
        {waiting ||
          (loading && (
            <div className="d-f ai-c jc-c mt-8">
              <Spinner />
            </div>
          ))}
        {lessonGraders.map(grader => (
          <article
            className="user owner quicklist-item"
            id={`grader_user_${grader.id}`}
            key={grader.id}
          >
            {grader.name}

            {!archived && (
              <button
                type="button"
                onClick={() => removeGrader(grader)}
                className="quicklist-remove u-resetBtn"
              >
                <Icon name="remove" />
              </button>
            )}
          </article>
        ))}
      </section>
    </div>
  );
};

const LessonGradersRoot = () => {
  return (
    <BrowserRouter>
      <Switch>
        <Route path="/lessons/:lessonId" component={LessonGraders} />
      </Switch>
    </BrowserRouter>
  );
};

export default props => <LessonGradersRoot {...props} />;
